import { httpError } from '../utils/userError';
import { getAuthToken } from '../utils/authToken';

// Energy Hub client (Energy HUB part 1). Mirrors usageService's shape — a thin
// fetch wrapper over the Bearer-authed REST surface — so the two monitoring
// clients stay recognisably the same.
//
// ⚠️ Every `*_pts` field in every response is a STRING with 2 decimals
// ("24000.00"). This layer passes them through UNTOUCHED: the display layer
// renders them verbatim and only converts to Number for geometry.

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

const getHeaders = () => {
    const token = getAuthToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

const handleResponse = async (response) => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: `API error: ${response.status}` } }));
        throw httpError(response.status, errorData);
    }
    return response.json();
};

/**
 * Build the shared date-range query.
 *
 * An OMITTED range is meaningful, not a bug: the BE defaults to the user's
 * current billing cycle, which is exactly what the picker's default ("This
 * cycle") wants — so we send nothing rather than computing cycle bounds
 * client-side and risking a mismatch with the server's own anniversary math.
 */
function _rangeParams({ startDate, endDate } = {}) {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    return params;
}

const _qs = (params) => {
    const s = params.toString();
    return s ? `?${s}` : '';
};

export const energyService = {
    /**
     * The tank: balance / gray / action points, the cycle window, plan,
     * lock + enforcement flags, and the storage footprint.
     * → { balance_pts, gray_pts, action_pts, cycle_opening_action_pts,
     *     cycle_deposit_pts, cycle_start, cycle_end, plan, locked, enforcement,
     *     unenforced, footprint_bytes, storage_days_reserved, clean_slate_at }
     */
    async getTank() {
        const response = await fetch(`${API_BASE_URL}/api/energy/tank`, { headers: getHeaders() });
        return handleResponse(response);
    },

    /**
     * The caller's recent SALARY cycles, newest first (#311) — the windows the
     * statement is read through. The client pages through what it is GIVEN
     * rather than re-deriving anniversary boundaries, which live in exactly one
     * place (BE `app/utils/plans`).
     * → { anchor, cycles: [{index, start, end, last_day, is_current}] }
     *   `end` is EXCLUSIVE (matches the tank's cycle_end, used for labels);
     *   `last_day` is INCLUSIVE (what the statement endpoints want).
     */
    async getCycles(limit) {
        const qs = limit ? `?limit=${encodeURIComponent(limit)}` : '';
        const response = await fetch(`${API_BASE_URL}/api/energy/cycles${qs}`, { headers: getHeaders() });
        return handleResponse(response);
    },

    /**
     * What the Gray Stack is MADE OF (#302) — the receipt behind the reservation.
     * ALWAYS returns all four categories in a fixed order, zeros included.
     * → { categories: [{category, bytes, pts_per_month}], total_bytes,
     *     total_pts_per_month, footprint_bytes }
     */
    async getStorageBreakdown() {
        const response = await fetch(`${API_BASE_URL}/api/energy/storage-breakdown`, { headers: getHeaders() });
        return handleResponse(response);
    },

    /**
     * Level 1 — the Pizza Chart. ALWAYS returns all five pillars (zeros included).
     * → { range: {start,end}, total_pts, pillars: [{pillar, pts, pct}] }
     */
    async getPillars(range = {}) {
        const response = await fetch(`${API_BASE_URL}/api/energy/pillars${_qs(_rangeParams(range))}`, { headers: getHeaders() });
        return handleResponse(response);
    },

    /**
     * The daily chart (#385) — one entry per UTC day of the range, each split by
     * pillar. Same range params as getPillars (omitted = the current cycle), and
     * the same rows: the days' totals sum to getPillars' total_pts exactly.
     * → { range: {start,end}, display_end, today, timezone: 'UTC', total_pts,
     *     days: [{date, future, total_pts, pillars: [{pillar, pts}]}] }
     *   A `future` day (the rest of the current cycle) carries null totals.
     */
    async getDaily(range = {}) {
        const response = await fetch(`${API_BASE_URL}/api/energy/daily${_qs(_rangeParams(range))}`, { headers: getHeaders() });
        return handleResponse(response);
    },

    /**
     * Level 2 — one pillar's categories.
     * → { pillar, pts, categories: [{category, pts}] }
     */
    async getPillarBreakdown(pillar, range = {}) {
        const response = await fetch(
            `${API_BASE_URL}/api/energy/pillars/${encodeURIComponent(pillar)}${_qs(_rangeParams(range))}`,
            { headers: getHeaders() },
        );
        return handleResponse(response);
    },

    /**
     * Level 3 — the atomic ledger for one category, paginated via next_offset.
     * → { pillar, category, total_pts,
     *     items: [{id, at, pipeline_step, pts, detail, conversation_id, document_id}],
     *     next_offset }
     */
    async getPillarItems(pillar, { category, startDate, endDate, limit, offset } = {}) {
        const params = _rangeParams({ startDate, endDate });
        if (category) params.set('category', category);
        if (limit != null) params.set('limit', String(limit));
        if (offset != null) params.set('offset', String(offset));
        const response = await fetch(
            `${API_BASE_URL}/api/energy/pillars/${encodeURIComponent(pillar)}/items${_qs(params)}`,
            { headers: getHeaders() },
        );
        return handleResponse(response);
    },

    // ── The Energy Receipt + the Context Thermostat (#349) ───────────────

    /**
     * ONE turn, itemized. Owner-only (a shared viewer gets a 404 — a receipt is
     * the owner's SPEND).
     * → { message_id, conversation_id, calls,
     *     categories: [{key, tokens, points}]   // always 4, in render order
     *     total_points,                          // POINTS only, never tokens
     *     tank_pct, tank_opening_pts,
     *     context_budget: {limits, conversation, user, effective} }
     */
    async getTurnReceipt(conversationId, messageId) {
        const response = await fetch(
            `${API_BASE_URL}/api/energy/receipt/${encodeURIComponent(conversationId)}/${encodeURIComponent(messageId)}`,
            { headers: getHeaders() },
        );
        return handleResponse(response);
    },

    /**
     * The thermostat's GLOBAL state, with no conversation in scope — what NEW
     * conversations will default to.
     * → { limits, conversation, user, effective }
     */
    async getContextBudget() {
        const response = await fetch(`${API_BASE_URL}/api/energy/context-budget`, {
            headers: getHeaders(),
        });
        return handleResponse(response);
    },

    /**
     * "Set as Global Default" — the per-user default, on the profile.
     * `null` clears it back to the system default; 0 is Total Recall.
     */
    async setGlobalContextBudget(tokens) {
        const response = await fetch(`${API_BASE_URL}/auth/profile`, {
            method: 'PATCH',
            headers: getHeaders(),
            body: JSON.stringify({ context_budget_tokens: tokens }),
        });
        return handleResponse(response);
    },

    /**
     * "Apply to this chat only" — the per-conversation override.
     * `null` clears it, so the chat inherits the global default again.
     */
    async setConversationContextBudget(conversationId, tokens) {
        const response = await fetch(
            `${API_BASE_URL}/conversations/${encodeURIComponent(conversationId)}`,
            {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ context_budget_tokens: tokens }),
            },
        );
        return handleResponse(response);
    },
};

export default energyService;
